import React, { useContext, useState, useEffect } from 'react';
import dayjs from 'dayjs';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import { Link, useHistory } from 'react-router-dom';

import { Button, Box } from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import QueryBuilderOutlinedIcon from '@mui/icons-material/QueryBuilderOutlined';
import CheckCircleOutlineRoundedIcon from '@mui/icons-material/CheckCircleOutlineRounded';

import { inboxMobileView, status } from 'venuescanner-js/constants/thread';
import { ViewContext } from 'venuescanner-js/react/components/Inbox/context/ViewContext';
import CtaMarkAsBooked from 'venuescanner-js/react/components/Inbox/components/Objects/Ctas/CtaMarkAsBooked';
import CtaShowToOrganiser from 'venuescanner-js/react/components/Inbox/components/Objects/Ctas/CtaShowToOrganiser';
import EnquiryMenu from 'venuescanner-js/react/components/Inbox/components/Enquiry/EnquiryMenu/EnquiryMenu';
import { HostEnquiryInfo } from 'venuescanner-js/react/components/Inbox/components/Enquiry/EnquiryInfo/HostEnquiryInfo';

import CtaNavBack from 'venuescanner-js/react/components/Inbox/components/Objects/Ctas/CtaNavBack';
import CallButton from 'venuescanner-js/react/components/Inbox/components/Objects/CallButton';
import EditButton from 'venuescanner-js/react/components/Inbox/components/Objects/EditButton';
import Tip from 'venuescanner-js/react/components/Inbox/components/Objects/Tip';

import HostBookedCTAs from '../HostBookedCTAs';
import CloseEnquiry from './CloseEnquiry';
import EnquiryCtas from '../EnquiryCtas';
import './EnquiryDetails.scss';

dayjs.extend(isSameOrBefore);

const HostEnquiryDetails = ({ can, send, state, status: enquiryStatus }) => {
  const { isMobileDevice, setActiveMobileView, activeUser } =
    useContext(ViewContext);
  const [hasCtas, setHasCtas] = useState(false);
  const { space, thread, venue } = state.context;
  const history = useHistory();
  const isBookingCompleted = thread?.status === status.BOOKED;

  useEffect(() => {
    if (can !== undefined) {
      setHasCtas(
        !!(
          can?.EDIT ||
          can?.ACCEPT ||
          can?.REACCEPT ||
          can?.BOOK ||
          can?.REOPEN ||
          can?.DECLINE ||
          can?.CANCEL ||
          can?.DECLINE_OR_CANCEL ||
          can?.SEND_PAYMENT_LINK
        ),
      );
    }
  }, [can]);

  return (
    <>
      <div className='enquiry enquiry-details'>
        <div className='enquiry__header'>
          {state.matches('booked') && isBookingCompleted && (
            <Tip
              sx={{ mb: 1.5, flex: '1 1 100%' }}
              icon={<CheckCircleOutlineRoundedIcon sx={{ color: '#fff' }} />}
              title='Event booked'
              text={
                isBookingCompleted
                  ? 'Congratulations! The event has been booked'
                  : ''
              }
            />
          )}
          {isMobileDevice && (
            <div>
              <Link
                to={history.location.pathname}
                onClick={() => setActiveMobileView(inboxMobileView.CHAT)}>
                <CtaNavBack />
              </Link>
            </div>
          )}

          <h1 className='enquiry__header-title'>Event enquiry details</h1>
          <div className='enquiry__header-cta'>
            <div className='enquiry-details__actions'>
              <div className='enquiry-details__buttons-container'>
                {!state.matches('cancelled') &&
                  !state.matches('booked') &&
                  !state.matches('expired') && (
                    <EditButton
                      isMobile={isMobileDevice}
                      thread={thread}
                      send={send}
                    />
                  )}
                {isMobileDevice && (
                  <CallButton
                    thread={thread}
                    venue={venue}
                    activeUser={activeUser}
                    isMobileDevice={isMobileDevice}
                  />
                )}
              </div>
            </div>
          </div>
        </div>

        <HostEnquiryInfo
          thread={thread}
          space={space}
          venue={venue}
          hasCtas={!isMobileDevice && hasCtas}
          state={state}
        />
        {state.matches('booked') && can.UNMARK_BOOKED_HOST && !isMobileDevice && (
          <Button
            sx={{
              fontSize: 14,
              lineHeight: 1.6,
              mt: 3.75,
              textDecoration: 'underline',
            }}
            variant='text'
            size='small'
            color='primary'
            onClick={() => send({ type: 'UNMARK_AS_BOOKED' })}>
            Unmark as booked
          </Button>
        )}
        {state.matches('booked') && isMobileDevice && (
          <HostBookedCTAs enquiryStatus={enquiryStatus} thread={thread} venue={venue} send={send} />
        )}
        {!isMobileDevice && (
          <Box sx={{ mt: 'auto', mb: 1 }}>
            {state.matches('open') && (
              <RequestQuoteCTAs thread={thread} can={can} send={send} />
            )}
            {state.matches('quoteRequested') && (
              <QuoteRequestedCTAs thread={thread} can={can} send={send} />
            )}
            {state.matches('quoteReceived') && (
              <QuoteReceivedCTAs thread={thread} can={can} send={send} />
            )}
            {state.matches('booked') && (
              <HostBookedCTAs enquiryStatus={enquiryStatus} thread={thread} venue={venue} send={send} />
            )}
            {state.matches('cancelled') && (
              <CancelledCTAs thread={thread} can={can} send={send} />
            )}
            {state.matches('expired') && (
              <ExpiredCTAs thread={thread} send={send} />
            )}
          </Box>
        )}
      </div>
      {isMobileDevice && (
        <EnquiryMenu thread={thread} state={state} send={send} />
      )}
    </>
  );
};

function RequestQuoteCTAs({ thread, can, send }) {
  const canDecline = can.DECLINE || can.CANCEL || can.DECLINE_OR_CANCEL;

  return (
    <EnquiryCtas>
      {(can.ACCEPT || can.REACCEPT) && (
        <Button
          fullWidth
          color='primary'
          variant='contained'
          size='medium-large'
          onClick={() => send({ type: 'EDIT_QUOTE' })}>
          Create Quote
        </Button>
      )}
      {can.MARK_BOOKED_HOST && (
        <CtaMarkAsBooked
          type='button'
          thread={thread}
          onClick={() => {
            send({ type: 'MARK_AS_BOOKED' });
          }}
        />
      )}
      {canDecline && (
        <CloseEnquiry
          fullWidth
          variant='text'
          color='primary'
          showTooltip={false}
          onClick={() => send({ type: 'CANCEL' })}
        />
      )}
      <Tip
        title='Tip'
        text='When you mark an enquiry as booked your venue gets a higher search ranking'
      />
      {thread.hide_from_organiser ? (
        <CtaShowToOrganiser thread={thread} />
      ) : null}
    </EnquiryCtas>
  );
}

function QuoteRequestedCTAs({ thread, can, send }) {
  return (
    <EnquiryCtas>
      {can.MARK_BOOKED_HOST && (
        <CtaMarkAsBooked
          thread={thread}
          variant='contained'
          type='button'
          onClick={() => {
            send({ type: 'MARK_AS_BOOKED' });
          }}
        />
      )}
      <Button
        fullWidth
        color='primary'
        variant={can.MARK_BOOKED_HOST ? 'outlined' : 'contained'}
        size='medium-large'
        onClick={() => send({ type: 'EDIT_QUOTE' })}>
        Send Quote
      </Button>
      <CloseEnquiry
        showTooltip={false}
        text='Close enquiry'
        variant='text'
        color='primary'
        onClick={() => send({ type: 'CANCEL' })}
      />
      <Tip
        title='Booking Requested'
        text='The organiser would like to book your venue'
        icon={<CheckCircleOutlineRoundedIcon sx={{ color: '#fff' }} />}
      />
      {thread.hide_from_organiser ? (
        <CtaShowToOrganiser thread={thread} />
      ) : null}
    </EnquiryCtas>
  );
}

function QuoteReceivedCTAs({ thread, can, send }) {
  return (
    <EnquiryCtas>
      {can.MARK_BOOKED_HOST && (
        <CtaMarkAsBooked
          thread={thread}
          variant='contained'
          type='button'
          onClick={() => {
            send({ type: 'MARK_AS_BOOKED' });
          }}
        />
      )}
      <Button
        fullWidth
        form='quoteForm'
        color='primary'
        variant='outlined'
        size='medium-large'
        onClick={() => send({ type: 'EDIT_QUOTE' })}>
        Edit quote
      </Button>

      <Button
        size='medium-large'
        fullWidth
        variant='text'
        color='primary'
        onClick={() => send({ type: 'CANCEL' })}>
        Close enquiry
      </Button>
      <Tip
        icon={<QueryBuilderOutlinedIcon sx={{ color: '#fbb400' }} />}
        text="We'll let you know when the organiser has accepted your quote"
        title='Awaiting organiser confirmation'
      />
      {thread.hide_from_organiser ? (
        <CtaShowToOrganiser thread={thread} />
      ) : null}
    </EnquiryCtas>
  );
}

function CancelledCTAs({ can, send }) {
  if (can.REOPEN) {
    return (
      <EnquiryCtas>
        <Button
          fullWidth
          color='primary'
          variant='contained'
          size='medium-large'
          onClick={() => send({ type: 'REOPEN_ENQUIRY' })}>
          Re-open Enquiry
        </Button>
      </EnquiryCtas>
    );
  }

  return (
    <Tip
      color='#eef0f4'
      icon={<CancelOutlinedIcon />}
      sx={{ paddingY: 3 }}
      title={<Box sx={{ color: '#424242' }}>Event closed</Box>}
    />
  );
}

function ExpiredCTAs({ thread, send }) {
  return (
    <EnquiryCtas>
      <Button
        color='primary'
        fullWidth
        variant='contained'
        size='medium-large'
        onClick={() => send({ type: 'MARK_AS_BOOKED' })}>
        Mark as booked
      </Button>
      <Button
        fullWidth
        color='primary'
        variant='outlined'
        size='medium-large'
        onClick={() => send({ type: 'NOT_A_BOOKING' })}>
        Not a booking
      </Button>
      <Tip
        title='Event date passed'
        icon={<WarningAmberIcon sx={{ color: '#fbb400' }} />}
        text='The date of this event has passed. Choose from the options above to
            close the enquiry.'
      />
      {thread.hide_from_organiser ? (
        <CtaShowToOrganiser thread={thread} />
      ) : null}
    </EnquiryCtas>
  );
}

export default HostEnquiryDetails;
